import React, { useState, useEffect, useContext, useReducer, useRef } from "react";
import { useHistory } from "react-router-dom";
import clsx from "clsx";
import { toast } from "react-toastify";

import { Paper, makeStyles, Card, CardHeader, Avatar, IconButton, Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, FormControl, InputLabel, Select, MenuItem, TextField, CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { blue, teal } from "@material-ui/core/colors";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PersonIcon from "@material-ui/icons/Person";
import RobotIcon from "../RobotIcon";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import TouchAppIcon from "@material-ui/icons/TouchApp";
import CallIcon from "@material-ui/icons/Call";
import Tooltip from "@material-ui/core/Tooltip";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import hasOpenOverlay from "../../helpers/hasOpenOverlay";
import { isDarkTheme } from "../../helpers/darkTint";
import { canSeeFullHistory } from "../../helpers/menuAccess";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { SocketContext } from "../../context/Socket/SocketContext";
import { useCall } from "../../context/Call/CallContext";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import {
  getManagedWhatsappIds,
  isAdminUser,
  isInactiveTicket,
  visibleWhatsappsForUser,
} from "../../helpers/canAccessTicket";
import InactiveConnectionBadge from "../InactiveConnectionBadge";
import MessageInput from "../MessageInputCustom/";
import ConnectionSelectDialog from "../ConnectionSelectDialog";
import QueueSelectDialog from "../QueueSelectDialog";
import TicketHeaderSkeleton from "../TicketHeaderSkeleton";
import ContactDrawer from "../ContactDrawer";
import MessageSearchDrawer from "../MessageSearchDrawer";
import MessagesList from "../MessagesList";
import ContactAvatar from "../ContactAvatar";
import TicketActionButtons from "../TicketActionButtonsCustom";
import { getClienteLabel } from "../TicketInfo";
import { ellipsisTicketHeader } from "../TicketHeader/compactStyles";
import { TagsContainer } from "../TagsContainer";
import ConnectionDisconnectedBanner from "../ConnectionDisconnectedBanner";
import { extractVariables } from "../../utils/templateValidator";
import { isPerConnectionContact } from "../../helpers/contactTicketMode";
import { isCallableWhatsapp } from "../../helpers/callableWhatsapp";
import { isWhatsappOnline } from "../../helpers/whatsappStatus";

const drawerWidth = 320;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },

  ticketHeader: {
    display: "flex",
    backgroundColor: theme.palette.tabHeaderBackground,
    flex: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
    },
    // Este cabeçalho é uma cópia do de TicketHeader (não reusa o componente),
    // então precisa importar a regra de reticências explicitamente — sem ela
    // um nome longo empurra os botões de ação para fora do painel.
    ...ellipsisTicketHeader(),
  },

  // Ver nota em Ticket/index.js: sem isto o `align-items: stretch` do header
  // estica o botão redondo e ele aparece como elipse.
  backButton: {
    marginLeft: 8,
    flex: "none",
    alignSelf: "center",
  },

  headerIconButton: {
    flex: "none",
    alignSelf: "center",
  },

  otherUserWarning: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    backgroundColor: isDarkTheme(theme) ? theme.palette.background.paper : "#f5f5f5",
    borderTop: `1px solid ${isDarkTheme(theme) ? theme.palette.divider : "#e0e0e0"}`,
  },

  warningText: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: isDarkTheme(theme) ? theme.palette.text.secondary : "#666",
    fontSize: "14px",
  },

  assumeButton: {
    backgroundColor: "#2196f3",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1976d2",
    },
  },

  // Cores fixas como o "Assumir"; no escuro o aviso fica escuro, então o texto clareia.
  newTicketButton: {
    marginTop: theme.spacing(1),
    color: isDarkTheme(theme) ? blue[300] : "#1976d2",
    borderColor: "#2196f3",
  },
}));

// Qual chamado exibir para uma conexão: o meu aberto tem prioridade, depois os
// de outro atendente / aguardando. Com `whatsappId` informado, considera apenas
// os chamados daquela conexão — cada conexão é uma conversa própria.
const resolveTicketForConnection = (tickets, whatsappId, userId, shouldUnify) => {
  const scoped = whatsappId
    ? (tickets || []).filter((t) => Number(t.whatsappId) === Number(whatsappId))
    : tickets || [];

  const mine = scoped.filter((t) => t.userId === userId && t.status === "open");
  if (mine.length > 0) {
    return { selectedTicket: mine[0], ticketsToShow: [] };
  }

  const others = scoped.filter((t) => t.userId !== userId && t.status === "open");
  const pending = scoped.filter((t) => t.status === "pending");
  const allOthers = [...others, ...pending];

  if (allOthers.length === 0) {
    return { selectedTicket: null, ticketsToShow: [] };
  }

  // Escopo de uma única conexão (ou unificação ligada): só há um chamado
  if (whatsappId || shouldUnify) {
    return { selectedTicket: allOthers[0], ticketsToShow: [allOthers[0]] };
  }

  // Sem conexão escolhida e sem unificação: um chamado por conexão
  const byWhatsapp = {};
  allOthers.forEach((ticket) => {
    if (!byWhatsapp[ticket.whatsappId]) byWhatsapp[ticket.whatsappId] = [];
    byWhatsapp[ticket.whatsappId].push(ticket);
  });
  const ticketsToShow = Object.values(byWhatsapp).map((list) => list[0]);
  return { selectedTicket: ticketsToShow[0], ticketsToShow };
};

const ContactChat = ({ contact, onClose, onTicketCreated }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = isDarkTheme(theme);
  const history = useHistory();
  const { user, menus } = useContext(AuthContext);
  const { connectionScopeEnabled } = useContext(SettingsContext);
  // Modo "Por ticket": só quem tem a permissão de histórico completo vê a opção
  // "Ver todas as mensagens" do seletor (o backend também ignora o viewAll).
  const canViewFullHistory = canSeeFullHistory(user, menus);
  const socketManager = useContext(SocketContext);

  // Filas do usuário — precisam ir em toda busca de /tickets: sem elas o
  // backend só devolve chamados sem fila (ver params das chamadas abaixo).
  const userQueueIds = (user?.queues || []).map(q => q.id);

  // Escopo estrito por conexão: usuário não-admin só envia/atende pelas
  // conexões gerenciadas (padrão ∪ atribuídas). `null` = sem restrição
  // (admin, escopo desligado, ou dados do usuário ainda não carregados).
  const scopedWhatsappIds =
    connectionScopeEnabled && !isAdminUser(user)
      ? getManagedWhatsappIds(user)
      : null;
  const restrictToManaged = Array.isArray(scopedWhatsappIds);

  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchDrawerOpen, setSearchDrawerOpen] = useState(false);

  // Seletor de conexão WhatsApp
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");

  // Com escopo estrito, o seletor de envio só oferece conexões gerenciadas.
  const visibleWhatsapps = restrictToManaged
    ? whatsapps.filter((w) => scopedWhatsappIds.includes(Number(w.id)))
    : whatsapps;

  // Se a conexão selecionada saiu do escopo (troca de usuário/atribuição),
  // limpa a seleção para o backend não receber um envio que será negado.
  useEffect(() => {
    if (!restrictToManaged || !selectedWhatsappId) return;
    if (!scopedWhatsappIds.includes(Number(selectedWhatsappId))) {
      setSelectedWhatsappId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictToManaged, selectedWhatsappId, JSON.stringify(scopedWhatsappIds)]);
  const [selectedCloudApiWindowOpen, setSelectedCloudApiWindowOpen] = useState(true);
  const [connDialogOpen, setConnDialogOpen] = useState(false);

  // Criar um "pseudo-ticket" para o MessagesList funcionar
  const [pseudoTicket, setPseudoTicket] = useState(null);
  
  // Estado para ticket aberto existente
  const [existingTicket, setExistingTicket] = useState(null);
  const [agentName, setAgentName] = useState("");
  const [aiTypingAgent, setAiTypingAgent] = useState(null);
  const aiTypingTimerRef = useRef(null);
  
  // Estado para múltiplos tickets de outros usuários (quando unificação está desabilitada)
  const [otherUserTickets, setOtherUserTickets] = useState([]);

  // Todos os chamados abertos/aguardando do contato (podem existir vários, um
  // por conexão) — é a partir desta lista que se resolve o chamado da conexão
  // selecionada no seletor.
  const [contactOpenTickets, setContactOpenTickets] = useState([]);
  
  // Estado da configuração de unificação
  const [unifyTickets, setUnifyTickets] = useState(true);
  // Chamado de grupo unificado (setting groupTicketUnify): só então o envio por
  // outra conexão MOVE o chamado do grupo (dica no cabeçalho)
  const [groupTicketUnified, setGroupTicketUnified] = useState(true);

  // Modo de histórico da empresa (setting messageHistory) e filtros do seletor
  // exibido no topo do histórico da aba de contato
  const [companyHistoryMode, setCompanyHistoryMode] = useState("contact");
  const [contactTickets, setContactTickets] = useState([]); // tickets do contato (seletor do modo ticket)
  const [historyTicketFilter, setHistoryTicketFilter] = useState("all"); // "all" | id do ticket
  const [historyWhatsappFilter, setHistoryWhatsappFilter] = useState("all"); // "all" | id da conexão

  // Cloud API - template picker state
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [cloudApiTemplates, setCloudApiTemplates] = useState([]);
  const [selectedCloudApiTemplate, setSelectedCloudApiTemplate] = useState("");
  const [cloudApiTemplateVars, setCloudApiTemplateVars] = useState([]);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templateTicket, setTemplateTicket] = useState(null); // ticket existente (ou null se ainda não existe)
  const [templateWhatsappId, setTemplateWhatsappId] = useState(null); // whatsappId para quando não há ticket
  const [drawerJump, setDrawerJump] = useState({ id: null, ts: null, ticketId: null, contactId: null });
  const [conflictTicketsModalOpen, setConflictTicketsModalOpen] = useState(false);
  const [conflictTickets, setConflictTickets] = useState([]);
  // Aviso de chamado com outro atendente: o backend diz se pode abrir novo
  // (grupo não pode) e o MessageInput manda o `retry` do envio que parou ali
  // (texto, áudio ou anexo), para a mensagem sair depois da escolha.
  const [conflictCanCreateNew, setConflictCanCreateNew] = useState(false);
  const conflictRetryRef = useRef(null);
  // "Abrir novo chamado" no aviso que substitui o input: o input volta e os
  // envios criam um chamado novo do atendente ao lado do outro.
  const [forceNewTicketMode, setForceNewTicketMode] = useState(false);
  const currentRenderedTicketId = existingTicket?.id || pseudoTicket?.id || null;

  // Sem chamado aberto o pseudo-ticket usa id string (`contact-<id>`), então a
  // comparação precisa ser textual: `Number("contact-1")` é NaN e NaN !== NaN,
  // o que descartava calado todo salto vindo da pesquisa de mensagens.
  // O contato também entra na comparação: no primeiro render após trocar de
  // contato, existingTicket/pseudoTicket ainda são os do anterior (o efeito que
  // limpa o salto só roda depois), e o MessagesList do contato novo montava
  // herdando o pulo — /contact/NOVO/around/MSG_DO_ANTERIOR → 404 → "não encontrada".
  const drawerJumpMatchesCurrent =
    !!drawerJump.id &&
    String(drawerJump.contactId) === String(contact?.id) &&
    drawerJump.ticketId != null &&
    currentRenderedTicketId != null &&
    String(drawerJump.ticketId) === String(currentRenderedTicketId);

  const activeJumpMessageId = drawerJumpMatchesCurrent ? drawerJump.id : null;
  const activeJumpMessageTs = drawerJumpMatchesCurrent ? drawerJump.ts : null;

  const getTemplateVariables = (template) => {
    const ordered = [];
    const addVars = (text) => {
      extractVariables(text || "").forEach((v) => {
        if (!ordered.includes(v)) {
          ordered.push(v);
        }
      });
    };

    if (template?.components?.length) {
      template.components.forEach((comp) => {
        const compType = String(comp.type || "").toUpperCase();
        const compFormat = String(comp.format || "").toUpperCase();
        if (compType === "BODY") {
          addVars(comp.text || template.bodyText || "");
        }
        if (compType === "HEADER" && compFormat === "TEXT") {
          addVars(comp.text || "");
        }
        if ((compType === "BUTTONS" || compType === "BUTTON") && Array.isArray(comp.buttons)) {
          comp.buttons.forEach((btn) => {
            const btnType = String(btn.type || "").toUpperCase();
            if (btnType === "URL") {
              addVars(btn.url || "");
            }
          });
        }
      });
    } else {
      addVars(template?.bodyText || "");
    }

    return ordered;
  };

  useEffect(() => {
    if (!contact?.id) return;

    // Reinicia filtros do seletor de histórico ao trocar de contato
    setForceNewTicketMode(false);
    setHistoryTicketFilter("all");
    setHistoryWhatsappFilter("all");
    setContactTickets([]);
    setContactOpenTickets([]);

    // PERF: só mostra spinner se a busca demorar >180ms (evita flash da bolinha
    // em respostas rápidas).
    let cancelled = false;
    const loadingTimer = setTimeout(() => {
      if (!cancelled) setLoading(true);
    }, 180);

    // Criar pseudo-ticket para exibir mensagens do contato
    // O MessagesList precisa de um objeto ticket com algumas propriedades
    const fetchContactData = async () => {
      try {
        // PERF: paralelizar os 4 GETs (eram sequenciais — somavam 1-3s).
        // Cada chamada é independente das outras.
        const [
          contactRes,
          settingsRes,
          whatsappsRes,
          ticketsRes
        ] = await Promise.all([
          api.get(`/contacts/${contact.id}`),
          api.get("/settings").catch(err => {
            console.error("Erro ao buscar configuração de unificação:", err);
            return { data: [] };
          }),
          api.get("/whatsapp"),
          api.get(`/tickets`, {
            params: {
              searchParam: "",
              pageNumber: 1,
              status: ["open", "pending"],
              contactId: contact.id,
              showAll: "true",
              // Sem queueIds o backend reduz o filtro a "tickets sem fila" e
              // devolve lista vazia — o chat ficaria sem os dados do chamado.
              queueIds: JSON.stringify(userQueueIds)
            }
          })
        ]);

        if (cancelled) return;

        const contactData = contactRes.data;

        const unifySetting = (settingsRes.data || []).find(s => s.key === "unifyTicketsByContact");
        const shouldUnify = !unifySetting || unifySetting.value !== "disabled";
        setUnifyTickets(shouldUnify);

        const groupUnifySetting = (settingsRes.data || []).find(s => s.key === "groupTicketUnify");
        setGroupTicketUnified(!groupUnifySetting || groupUnifySetting.value !== "disabled");

        // Modo de histórico da empresa (contact | ticket | whatsapp)
        const historySetting = (settingsRes.data || []).find(s => s.key === "messageHistory");
        let resolvedHistoryMode = "contact";
        if (["contact", "ticket", "whatsapp"].includes(historySetting?.value)) {
          resolvedHistoryMode = historySetting.value;
        } else if (historySetting?.value === "disabled") {
          resolvedHistoryMode = "ticket";
        }
        setCompanyHistoryMode(resolvedHistoryMode);

        // Modo ticket: carrega os tickets do contato (todas as situações) para o
        // seletor "ver mensagens de um ticket específico"
        if (resolvedHistoryMode === "ticket") {
          try {
            const { data: allTicketsData } = await api.get(`/tickets`, {
              params: {
                pageNumber: 1,
                status: ["open", "pending", "closed"],
                contactId: contact.id,
                showAll: "true",
                queueIds: JSON.stringify(userQueueIds)
              }
            });
            if (!cancelled) {
              setContactTickets(
                (allTicketsData.tickets || []).filter(t => t.contactId === contact.id)
              );
            }
          } catch (ticketListErr) {
            if (!cancelled) setContactTickets([]);
          }
        }

        const whatsappsData = visibleWhatsappsForUser(
          user,
          whatsappsRes.data || [],
          connectionScopeEnabled
        );
        setWhatsapps(whatsappsData);
        const connectedWhatsapps = whatsappsData.filter(w => w.status === "CONNECTED");
        console.log(`[ContactChat] Conexões ativas:`, connectedWhatsapps.map(w => `${w.id} - ${w.name}`));

        const ticketsData = ticketsRes.data;

        const openTickets = ticketsData.tickets?.filter(t => 
          t.contactId === contact.id && 
          (t.status === "open" || t.status === "pending")
        ) || [];
        
        console.log(`[ContactChat] Tickets abertos encontrados:`, openTickets.length);
        openTickets.forEach(t => {
          console.log(`  - Ticket #${t.id}: whatsappId=${t.whatsappId}, userId=${t.userId}, status=${t.status}`);
        });
        
        setContactOpenTickets(openTickets);

        // Conexão inicial no modo por conexão: prioriza a que já tem chamado
        // aberto para o contato (assim abre direto no atendimento em andamento),
        // senão a conexão padrão do usuário, senão a primeira que ele controla.
        //
        // Chamado "um por conexão" (unifyTicketsByContact=disabled, contato 1:1)
        // sempre fixa uma conexão, e a do atendente vem primeiro: sem conexão o
        // chat exibiria (e enviaria no) chamado de outra conexão.
        const perConnectionTickets = !shouldUnify && isPerConnectionContact(contactData);
        let initialWhatsappId = selectedWhatsappId;
        if (resolvedHistoryMode === "whatsapp" || perConnectionTickets) {
          const managedIds = getManagedWhatsappIds(user) || [];
          const selectable = managedIds.length
            ? whatsappsData.filter(w => managedIds.includes(Number(w.id)))
            : whatsappsData;

          if (selectable.length > 0) {
            const connWithTicket = selectable.find(w =>
              openTickets.some(t => Number(t.whatsappId) === Number(w.id))
            );
            const userConn = selectable.find(w => Number(w.id) === Number(user?.whatsappId));
            const initialConn = perConnectionTickets
              ? userConn ||
                selectable.find(w =>
                  openTickets.some(t =>
                    Number(t.whatsappId) === Number(w.id) && t.userId === user?.id && t.status === "open"
                  )
                ) ||
                selectable.find(w => w.isDefault) ||
                selectable[0]
              : connWithTicket || userConn || selectable[0];

            initialWhatsappId = initialConn.id;
            // Fora do histórico por conexão o filtro do histórico continua "todas".
            if (resolvedHistoryMode === "whatsapp") setHistoryWhatsappFilter(initialConn.id);
            setSelectedWhatsappId(initialConn.id);
          }
        }

        // Determinar qual ticket exibir (escopo da conexão selecionada)
        const { selectedTicket, ticketsToShow } = resolveTicketForConnection(
          openTickets,
          initialWhatsappId,
          user.id,
          shouldUnify
        );

        console.log(
          `[ContactChat] Chamado exibido: ${selectedTicket ? `#${selectedTicket.id}` : "nenhum"}` +
          ` (conexão ${initialWhatsappId || "automática"})`
        );

        if (selectedTicket) {
          setExistingTicket(selectedTicket);
          setOtherUserTickets(ticketsToShow);
          setPseudoTicket({
            ...selectedTicket,
            contact: contactData,
            _isContactChat: true,
          });
        } else {
          // Nenhum ticket aberto
          console.log(`[ContactChat] Nenhum ticket aberto - criando pseudo-ticket`);
          setExistingTicket(null);
          setOtherUserTickets([]);
          setPseudoTicket({
            id: `contact-${contact.id}`,
            contactId: contact.id,
            contact: contactData,
            status: "closed",
            isGroup: contactData.isGroup || false,
            whatsapp: null,
            queue: null,
            user: null,
            _isContactChat: true,
          });
        }
        
        clearTimeout(loadingTimer);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        clearTimeout(loadingTimer);
        toastError(err);
        setLoading(false);
      }
    };

    fetchContactData();

    return () => {
      cancelled = true;
      clearTimeout(loadingTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, user.id, connectionScopeEnabled]);

  // Conexões exibidas no seletor de histórico (modo whatsapp): só as que o
  // usuário controla — espelho do escopo que o backend aplica às mensagens.
  const managedWhatsappIds = getManagedWhatsappIds(user) || [];
  const historySelectorWhatsapps = managedWhatsappIds.length
    ? whatsapps.filter((w) => managedWhatsappIds.includes(Number(w.id)))
    : whatsapps;

  const selectedConnection = whatsapps.find(
    (w) => Number(w.id) === Number(selectedWhatsappId)
  );

  // Grupo tem um único chamado (em qualquer conexão). Se ele vive em outra
  // conexão que não a selecionada, avisa que o envio vai movê-lo — quem move é
  // o backend (ResolveGroupTicketService) ao enviar com a conexão explícita.
  const contactIsGroup = Boolean(contact?.isGroup || pseudoTicket?.contact?.isGroup);
  const groupTicketElsewhere =
    contactIsGroup && groupTicketUnified && selectedWhatsappId
      ? (contactOpenTickets || []).find(
          (t) =>
            (t.status === "open" || t.status === "pending") &&
            t.whatsappId &&
            Number(t.whatsappId) !== Number(selectedWhatsappId)
        )
      : null;
  const groupTicketElsewhereConnName = groupTicketElsewhere
    ? groupTicketElsewhere.whatsapp?.name ||
      whatsapps.find((w) => Number(w.id) === Number(groupTicketElsewhere.whatsappId))?.name ||
      `#${groupTicketElsewhere.whatsappId}`
    : "";

  // Escolher a conexão (pelo seletor do topo ou pelo popup) é uma decisão só:
  // define por onde a mensagem sai e qual histórico/chamado aparece.
  const handleConnectionChange = (id) => {
    setSelectedWhatsappId(id);
    setForceNewTicketMode(false);
    if (
      companyHistoryMode === "whatsapp" &&
      id &&
      historySelectorWhatsapps.some((w) => Number(w.id) === Number(id))
    ) {
      setHistoryWhatsappFilter(id);
    }
  };

  // Ligar sem chamado na conexão: o chamado nasce no backend com a regra do
  // envio por contato (reaproveita o meu, assume o Aguardando, senão cria um
  // aberto meu), só depois do microfone liberado (startCallFromContact).
  // Conexão e fila são escolhidas antes, como no envio.
  const { canUseCalls, startCallFromContact } = useCall();
  const [callConnDialogOpen, setCallConnDialogOpen] = useState(false);
  const [callQueueWhatsappId, setCallQueueWhatsappId] = useState(null);

  // Contato em tela agora (null depois de desmontar): até o microfone ser
  // liberado o atendente pode ter trocado de contato ou saído da aba.
  const shownContactIdRef = useRef(null);
  useEffect(() => {
    shownContactIdRef.current = contact?.id ?? null;
    return () => {
      shownContactIdRef.current = null;
    };
  }, [contact?.id]);

  const callableWhatsapps = visibleWhatsapps.filter(
    (w) => isCallableWhatsapp(w) && isWhatsappOnline(w)
  );
  const showCallWithoutTicket =
    canUseCalls &&
    Boolean(pseudoTicket) &&
    !existingTicket?.id &&
    !contactIsGroup &&
    callableWhatsapps.length > 0;

  const startCallWithoutTicket = (whatsappId, queueId) => {
    const callContact = pseudoTicket?.contact || contact;

    startCallFromContact(callContact, {
      prepareTicket: async () => {
        try {
          const { data } = await api.post(`/calls/contact/${callContact.id}/ticket`, {
            whatsappId,
            queueId,
          });
          const ticket = data?.ticket;
          if (!ticket?.id) return null;

          if (shownContactIdRef.current === callContact.id) {
            // A conversa exibida acompanha a conexão em que a ligação sai.
            if (Number(ticket.whatsappId) !== Number(selectedWhatsappId)) {
              handleConnectionChange(ticket.whatsappId);
            }
            showTicketInChat(ticket);
          }
          return ticket;
        } catch (err) {
          toastError(err);
          return null;
        }
      },
    });
  };

  // Fila do chamado novo, como o resolveQueueForNewTicket do MessageInput: sem
  // fila ou com uma só não pergunta.
  const chooseCallQueue = (whatsappId) => {
    const userQueues = user?.queues || [];
    if (userQueues.length > 1) {
      setCallQueueWhatsappId(whatsappId);
      return;
    }
    startCallWithoutTicket(whatsappId, userQueues[0]?.id);
  };

  // A conexão escolhida no seletor vale se ligar por ela; senão, com uma só
  // ligável ela é usada direto e com mais de uma o atendente escolhe.
  const handleCallWithoutTicket = () => {
    const selectedCallable = callableWhatsapps.find(
      (w) => Number(w.id) === Number(selectedWhatsappId)
    );
    const whatsapp =
      selectedCallable || (callableWhatsapps.length === 1 ? callableWhatsapps[0] : null);

    if (whatsapp) {
      chooseCallQueue(whatsapp.id);
    } else {
      setCallConnDialogOpen(true);
    }
  };

  // Sem a opção "Todas as minhas conexões", o filtro precisa apontar sempre
  // para uma conexão concreta: a padrão do usuário, senão a primeira da lista.
  useEffect(() => {
    if (companyHistoryMode !== "whatsapp") return;
    if (historySelectorWhatsapps.length === 0) return;
    const isValid = historySelectorWhatsapps.some(
      (w) => Number(w.id) === Number(historyWhatsappFilter)
    );
    if (!isValid) {
      const defaultConn =
        historySelectorWhatsapps.find(
          (w) => Number(w.id) === Number(user?.whatsappId)
        ) || historySelectorWhatsapps[0];
      setHistoryWhatsappFilter(defaultConn.id);
      // Mantém o seletor de envio (ícone de telefone) espelhando o histórico
      setSelectedWhatsappId((prev) => prev || defaultConn.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyHistoryMode, whatsapps, historyWhatsappFilter, user?.whatsappId]);

  // Sem a permissão de histórico completo não existe "Ver todas as mensagens":
  // o filtro aponta sempre para um ticket concreto, o mais recente do contato.
  useEffect(() => {
    if (companyHistoryMode !== "ticket" || canViewFullHistory) return;
    if (contactTickets.length === 0) return;
    const isValid = contactTickets.some(
      (t) => String(t.id) === String(historyTicketFilter)
    );
    if (!isValid) {
      const latest = contactTickets.reduce((a, b) =>
        Number(b.id) > Number(a.id) ? b : a
      );
      setHistoryTicketFilter(latest.id);
    }
  }, [companyHistoryMode, canViewFullHistory, contactTickets, historyTicketFilter]);

  // Trocar de conexão troca de conversa: exibe o chamado aberto naquela conexão
  // (com header, tags e botões do ticket) ou o estado "sem chamado", que abre um
  // novo ao enviar a primeira mensagem.
  useEffect(() => {
    if (!contact?.id || !selectedWhatsappId) return;

    const { selectedTicket, ticketsToShow } = resolveTicketForConnection(
      contactOpenTickets,
      selectedWhatsappId,
      user.id,
      unifyTickets
    );

    setOtherUserTickets(ticketsToShow);

    if (selectedTicket) {
      setExistingTicket(selectedTicket);
      setPseudoTicket((prev) => ({
        ...selectedTicket,
        contact: prev?.contact || contact,
        _isContactChat: true,
      }));
    } else {
      setExistingTicket(null);
      setPseudoTicket((prev) => {
        const contactData = prev?.contact || contact;
        return {
          id: `contact-${contact.id}`,
          contactId: contact.id,
          contact: contactData,
          status: "closed",
          isGroup: contactData?.isGroup || false,
          whatsapp: null,
          queue: null,
          user: null,
          _isContactChat: true,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, selectedWhatsappId, contactOpenTickets, unifyTickets, user.id]);

  useEffect(() => {
    let isMounted = true;

    const checkCloudApiWindow = async () => {
      if (!contact?.id || !selectedWhatsappId) {
        if (isMounted) setSelectedCloudApiWindowOpen(true);
        return;
      }

      const selectedConnection = whatsapps.find(
        (w) => Number(w.id) === Number(selectedWhatsappId)
      );

      if (!selectedConnection || selectedConnection.type !== "cloudapi") {
        if (isMounted) setSelectedCloudApiWindowOpen(true);
        return;
      }

      try {
        const { data } = await api.get("/cloudapi/window-by-contact", {
          params: {
            contactId: contact.id,
            whatsappId: selectedConnection.id,
          },
        });

        if (isMounted) {
          setSelectedCloudApiWindowOpen(Boolean(data?.isOpen));
        }
      } catch (_err) {
        if (isMounted) setSelectedCloudApiWindowOpen(true);
      }
    };

    checkCloudApiWindow();

    return () => {
      isMounted = false;
    };
  }, [contact?.id, selectedWhatsappId, whatsapps]);

  // Lido pelo handler de `connectionInactivated` sem entrar nas deps do socket.
  const isAdminRef = useRef(false);
  isAdminRef.current = isAdminUser(user);

  // Socket para atualizações do contato e ticket
  useEffect(() => {
    if (!contact?.id) return;

    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" && data.contact?.id === contact.id) {
        setPseudoTicket(prev => prev ? {
          ...prev,
          contact: { ...prev.contact, ...data.contact }
        } : prev);
      }
    });

    // Escutar atualizações de ticket para liberar a tela quando o ticket for fechado
    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "update" && data.ticket?.contactId === contact.id) {
        // Mantém a lista de chamados por conexão em dia (o contato pode ter
        // chamados abertos em mais de uma conexão ao mesmo tempo)
        setContactOpenTickets(prev => {
          const rest = (prev || []).filter(t => Number(t.id) !== Number(data.ticket.id));
          return data.ticket.status === "closed" ? rest : [...rest, data.ticket];
        });

        // Só mexe na tela se o evento for do chamado que está sendo exibido —
        // evento de chamado de OUTRA conexão não pode trocar a conversa aberta
        if (Number(existingTicket?.id) !== Number(data.ticket.id)) {
          return;
        }

        // Se o ticket foi fechado ou mudou de usuário, atualizar o estado
        if (data.ticket.status === "closed") {
          // Ticket foi fechado, limpar o existingTicket para liberar a tela
          setExistingTicket(null);
          setPseudoTicket(prev => prev ? {
            ...prev,
            id: `contact-${contact.id}`,
            status: "closed",
            userId: null,
            user: null,
          } : prev);
        } else {
          // Ticket foi atualizado (mudou de usuário, etc)
          setExistingTicket(prev => ({
            ...(prev || {}),
            ...data.ticket,
            prompt: data.ticket.prompt || prev?.prompt,
          }));
          setPseudoTicket(prev => prev ? {
            ...prev,
            ...data.ticket,
            contact: prev.contact,
            prompt: data.ticket.prompt || prev.prompt,
            _isContactChat: true,
          } : prev);
        }
      }
      
      // Se o ticket foi deletado
      if (data.action === "delete") {
        setContactOpenTickets(prev =>
          (prev || []).filter(t => Number(t.id) !== Number(data.ticketId))
        );
      }

      // Conexão excluída: os chamados dela foram fechados e inativados no backend.
      // Para o chamado em tela vale o mesmo tratamento de "fechado".
      if (data.action === "connectionInactivated" && data.whatsappId) {
        const inactivatedWhatsappId = Number(data.whatsappId);
        setContactOpenTickets(prev =>
          (prev || []).filter(t => Number(t.whatsappId) !== inactivatedWhatsappId)
        );
        setContactTickets(prev =>
          isAdminRef.current
            ? (prev || []).map(t =>
                Number(t.whatsappId) === inactivatedWhatsappId
                  ? { ...t, status: "closed", active: false, inactivatedAt: data.inactivatedAt || t.inactivatedAt }
                  : t
              )
            : (prev || []).filter(t => Number(t.whatsappId) !== inactivatedWhatsappId)
        );
        if (Number(existingTicket?.whatsappId) === inactivatedWhatsappId) {
          setExistingTicket(null);
          setPseudoTicket(prev => prev ? {
            ...prev,
            id: `contact-${contact.id}`,
            status: "closed",
            userId: null,
            user: null,
          } : prev);
        }
      }

      if (data.action === "delete" && existingTicket?.id === data.ticketId) {
        setExistingTicket(null);
        setPseudoTicket(prev => prev ? {
          ...prev,
          id: `contact-${contact.id}`,
          status: "closed",
          userId: null,
          user: null,
        } : prev);
      }
    });

    // Status "Agente de IA está digitando..." (mesmo evento do fluxo real)
    socket.on(`company-${companyId}-aiHubTyping`, (data) => {
      if (!data || Number(data.ticketId) !== Number(existingTicket?.id)) return;
      if (aiTypingTimerRef.current) {
        clearTimeout(aiTypingTimerRef.current);
        aiTypingTimerRef.current = null;
      }
      if (data.typing) {
        setAiTypingAgent(data.agentName || "IA");
        aiTypingTimerRef.current = setTimeout(() => setAiTypingAgent(null), 60000);
      } else {
        setAiTypingAgent(null);
      }
    });

    return () => {
      if (aiTypingTimerRef.current) {
        clearTimeout(aiTypingTimerRef.current);
        aiTypingTimerRef.current = null;
      }
      socket.disconnect();
    };
  }, [contact?.id, socketManager, existingTicket?.id, existingTicket?.whatsappId]);

  const handleDrawerOpen = () => {
    setSearchDrawerOpen(false);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleSearchDrawerOpen = () => {
    setDrawerOpen(false);
    setSearchDrawerOpen(true);
  };

  const handleSearchDrawerClose = () => {
    setSearchDrawerOpen(false);
  };

  // Ao trocar de contato, fecha o painel de pesquisa e descarta um salto
  // pendente — senão o contato recém-aberto herdaria o pulo do anterior.
  useEffect(() => {
    setSearchDrawerOpen(false);
    setDrawerJump({ id: null, ts: null, ticketId: null, contactId: null });
  }, [contact?.id]);

  const handleJumpToMessageFromDrawer = (messageId) => {
    if (!messageId) return;
    setDrawerJump({
      id: String(messageId),
      ts: Date.now(),
      ticketId: currentRenderedTicketId,
      contactId: contact?.id ?? null,
    });
  };

  // Verifica se o ticket existente pertence a outro usuário (funciona para todos, incluindo admin)
  const isOtherUserTicket = () => {
    if (!existingTicket || !existingTicket.userId) return false;
    const isOther = existingTicket.userId !== user.id && existingTicket.status === "open";
    console.log(`[ContactChat] isOtherUserTicket: ticketUserId=${existingTicket.userId}, currentUserId=${user.id}, status=${existingTicket.status}, isOther=${isOther}`);
    return isOther;
  };

  const isAgentControlledTicket = () => {
    if (!existingTicket || !existingTicket.id) return false;
    return (existingTicket.status === "open" || existingTicket.status === "pending") && !!existingTicket.promptId;
  };

  // ESC fecha a conversa do contato (volta para a lista), igual ao WhatsApp Web.
  // O rodapé de digitação e os dialogs do MUI consomem o ESC antes daqui.
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (hasOpenOverlay()) return;

      if (searchDrawerOpen) {
        setSearchDrawerOpen(false);
      } else if (drawerOpen) {
        setDrawerOpen(false);
      } else if (typeof onClose === "function") {
        onClose();
      } else {
        return;
      }

      e.preventDefault();
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose, searchDrawerOpen, drawerOpen]);

  useEffect(() => {
    let cancelled = false;

    const resolveAgentName = async () => {
      const promptId = existingTicket?.promptId;
      const promptNameFromTicket = existingTicket?.prompt?.name;

      if (!promptId) {
        if (!cancelled) setAgentName("");
        return;
      }

      if (promptNameFromTicket) {
        if (!cancelled) setAgentName(promptNameFromTicket);
        return;
      }

      try {
        const { data } = await api.get(`/prompt/${promptId}`);
        if (!cancelled) {
          setAgentName(data?.name || i18n.t("contactChat.agent.fallbackName", { id: promptId }));
        }
      } catch (_err) {
        if (!cancelled) {
          setAgentName(i18n.t("contactChat.agent.fallbackName", { id: promptId }));
        }
      }
    };

    resolveAgentName();

    return () => {
      cancelled = true;
    };
  }, [existingTicket?.promptId, existingTicket?.prompt?.name]);

  // Função para assumir um chamado específico
  const handleAssumeTicket = async (ticketToAssume) => {
    if (!ticketToAssume?.id) return;
    
    try {
      // Escopo estrito: só assume chamado de conexão gerenciada pelo usuário.
      if (
        restrictToManaged &&
        ticketToAssume.whatsappId &&
        !scopedWhatsappIds.includes(Number(ticketToAssume.whatsappId))
      ) {
        toast.error(
          i18n.t("contactChat.errors.connectionNotAssigned")
        );
        return;
      }

      // Verificar se o ticket tem fila e se o usuário tem acesso a ela
      if (ticketToAssume.queueId) {
        const userQueueIds = user.queues.map((q) => q.id);
        if (!userQueueIds.includes(ticketToAssume.queueId)) {
          toast.error(i18n.t("contactChat.errors.noQueueAccess"));
          return;
        }
      } else if (!isAdminUser(user) && user.allTicket !== "enabled") {
        // Ticket sem fila exige allTicket — gerenciar a conexão não libera.
        toast.error(
          i18n.t("contactChat.errors.noQueuelessPermission")
        );
        return;
      }

      await api.put(`/tickets/${ticketToAssume.id}`, {
        userId: user.id,
        status: "open",
        transferredBy: user.id,
        promptId: null,
      });
      toast.success(i18n.t("contactChat.toasts.ticketAssumed"));
      setConflictTicketsModalOpen(false);
      setConflictTickets([]);

      // Assumiu pelo aviso de um envio: a mensagem (ou o áudio) sai agora no
      // chamado assumido — a rota por contato reaproveita o chamado aberto do
      // próprio atendente.
      const pendingRetry = conflictRetryRef.current;
      conflictRetryRef.current = null;
      if (pendingRetry) {
        try {
          await pendingRetry();
        } catch (retryErr) {
          toastError(retryErr);
        }
      }

      // Redirecionar para o ticket
      if (onTicketCreated) {
        onTicketCreated(ticketToAssume.uuid);
      } else {
        history.push(`/tickets/${ticketToAssume.uuid}`);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseConflictTicketsModal = () => {
    setConflictTicketsModalOpen(false);
    setConflictTickets([]);
    // Cancelar mantém o texto no input e o áudio guardado no MessageInput.
    conflictRetryRef.current = null;
  };

  // "Abrir novo chamado" no aviso de um envio: reenvia a mesma mensagem com
  // forceNewTicket — o backend cria um chamado do atendente ao lado do outro.
  const handleCreateNewFromConflict = async () => {
    const pendingRetry = conflictRetryRef.current;
    handleCloseConflictTicketsModal();
    if (!pendingRetry) {
      setForceNewTicketMode(true);
      return;
    }
    try {
      await pendingRetry({ forceNewTicket: true });
    } catch (err) {
      toastError(err);
    }
  };

  // === Cloud API Template Picker Handlers ===
  const handleOpenTemplatePicker = async (ticketOrInfo) => {
    // ticketOrInfo pode ser um ticket ({ id, uuid, whatsappId }) ou { whatsappId, contactId } quando não há ticket
    const whatsappId = ticketOrInfo?.whatsappId;
    if (ticketOrInfo?.id) {
      setTemplateTicket(ticketOrInfo);
    } else {
      setTemplateTicket(null);
    }
    setTemplateWhatsappId(whatsappId);
    setLoadingTemplates(true);
    setTemplatePickerOpen(true);
    try {
      if (!whatsappId) return;
      // Resolver wabaId a partir do whatsappId para carregar templates da WABA
      const conn = whatsapps.find(w => w.id === Number(whatsappId));
      const queryParams = conn?.cloudApiBusinessAccountId
        ? { wabaId: conn.cloudApiBusinessAccountId }
        : { whatsappId };
      const { data } = await api.get("/message-templates", { params: queryParams });
      setCloudApiTemplates(
        (data.templates || data).filter((t) => t.status === "APPROVED")
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleSelectCloudApiTemplate = (templateId) => {
    setSelectedCloudApiTemplate(templateId);
    const template = cloudApiTemplates.find((t) => t.id === parseInt(templateId));
    if (template) {
      const varsList = getTemplateVariables(template);
      const vars = varsList.map((v) => ({ name: v, value: "" }));
      setCloudApiTemplateVars(vars);
    } else {
      setCloudApiTemplateVars([]);
    }
  };

  const handleSendCloudApiTemplate = async () => {
    if (!selectedCloudApiTemplate) return;
    setSendingTemplate(true);
    try {
      let variables = undefined;
      if (cloudApiTemplateVars.length > 0) {
        variables = cloudApiTemplateVars.map((v) => v.value || "");
      }

      // Montar payload: com ticketId (ticket existente) ou contactId+whatsappId (criar ticket)
      const payload = { variables };
      if (templateTicket?.id) {
        payload.ticketId = templateTicket.id;
      } else {
        payload.contactId = contact.id;
        payload.whatsappId = templateWhatsappId;
      }

      const { data } = await api.post(`/message-templates/${selectedCloudApiTemplate}/send`, payload);
      toast.success(i18n.t("contactChat.templatePicker.sent"));
      setTemplatePickerOpen(false);
      setSelectedCloudApiTemplate("");
      setCloudApiTemplateVars([]);
      // Navigate to the ticket (criado ou existente)
      const ticketUuid = templateTicket?.uuid || data.ticket?.uuid;
      if (ticketUuid) {
        if (onTicketCreated) {
          onTicketCreated(ticketUuid);
        } else {
          history.push(`/tickets/${ticketUuid}`);
        }
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSendingTemplate(false);
    }
  };

  const handleCloseTemplatePicker = () => {
    setTemplatePickerOpen(false);
    setSelectedCloudApiTemplate("");
    setCloudApiTemplateVars([]);
    // Se já existia um ticket, navegar para ele
    if (templateTicket?.uuid) {
      if (onTicketCreated) {
        onTicketCreated(templateTicket.uuid);
      } else {
        history.push(`/tickets/${templateTicket.uuid}`);
      }
    }
    setTemplateTicket(null);
    setTemplateWhatsappId(null);
  };

  // Chamado criado ou assumido aqui (primeira mensagem, ligação): atualizamos o
  // estado local SEM navegar. Antes chamavamos onTicketCreated ->
  // history.push('/tickets/uuid'), o que desmontava o ContactChat e remontava o
  // componente Ticket (refazendo todos os fetches de mensagens/contato). Agora
  // apenas promovemos o pseudoTicket ao ticket real - o MessagesList tem `key`
  // estavel (`contact-${id}`), entao nao remonta, e o header passa a exibir o
  // numero do ticket.
  // A URL fica como esta de proposito: o parent so troca para o componente
  // Ticket quando o ticketId aparece nela, e aqui queremos seguir no ContactChat.
  const showTicketInChat = (ticket) => {
    if (!ticket?.uuid || ticket.status === "closed") return;

    // O chamado do atendente já existe: os próximos envios vão nele.
    setForceNewTicketMode(false);
    setExistingTicket(prev =>
      Number(prev?.id) === Number(ticket.id) ? { ...prev, ...ticket } : ticket
    );
    // Registra o chamado na lista por conexao, senao ao voltar para esta
    // conexao o chat apareceria como "sem chamado"
    setContactOpenTickets(prev => {
      const list = prev || [];
      const previous = list.find(t => Number(t.id) === Number(ticket.id));
      const rest = list.filter(t => Number(t.id) !== Number(ticket.id));
      return [...rest, { ...(previous || {}), ...ticket }];
    });
    setPseudoTicket(prev => ({
      ...(prev || {}),
      ...ticket,
      contact: prev?.contact || contact,
      _isContactChat: true,
    }));
  };

  // Chamado pelo MessageInput DEPOIS do envio: o POST é sempre dele, para que o
  // chat de contato use o mesmo caminho (e o mesmo pipeline no backend) da tela
  // de Ticket. Aqui só reagimos ao resultado.
  const handleMessageSent = (data) => {
    if (!data) return;

    console.log("[ContactChat] Envio concluído", {
      requiresTemplate: !!data?.requiresTemplate,
      ticket: data?.ticket
        ? {
            id: data.ticket.id,
            uuid: data.ticket.uuid,
            whatsappId: data.ticket.whatsappId,
            contactId: data.ticket.contactId,
          }
        : null,
      whatsappId: data?.whatsappId,
      contactId: data?.contactId,
      selectedWhatsappId,
    });

    // Cloud API: requer template para iniciar conversa
    if (data.requiresTemplate) {
      // Pode ter ticket (janela fechada) ou não (novo ticket Cloud API)
      if (data.ticket) {
        handleOpenTemplatePicker(data.ticket);
      } else {
        handleOpenTemplatePicker({ whatsappId: data.whatsappId, contactId: data.contactId });
      }
      return;
    }

    showTicketInChat(data.ticket);
  };

  // Erros de envio que sao desta tela. O input so cai no toast generico quando
  // devolvemos false aqui.
  // `retry` (do MessageInput) reenvia a mesma mensagem depois da escolha no
  // aviso de chamado com outro atendente.
  const handleSendError = (err, { retry } = {}) => {
    const errorCode = err?.response?.data?.error;

    // Só o 403 de escopo de conexão vira este aviso. Outros 403 (fila sem
    // acesso, agente de IA no chamado) têm mensagem própria no toast.
    if (errorCode === "ERR_NO_PERMISSION") {
      toast.error(
        i18n.t("contactChat.errors.conversationConnectionNotAssigned")
      );
      return true;
    }

    if (errorCode === "ERR_TICKETS_FROM_OTHER_USERS") {
      const details = err?.response?.data?.details;
      let parsedTickets = [];
      let canCreateNew = false;

      try {
        const parsedDetails = typeof details === "string" ? JSON.parse(details) : details;
        parsedTickets = Array.isArray(parsedDetails?.tickets) ? parsedDetails.tickets : [];
        canCreateNew = parsedDetails?.canCreateNew === true;
      } catch (_parseErr) {
        parsedTickets = [];
      }

      if (parsedTickets.length > 0) {
        // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: substituir toast por modal com opcao de assumir chamado.
        conflictRetryRef.current = typeof retry === "function" ? retry : null;
        setConflictCanCreateNew(canCreateNew);
        setConflictTickets(parsedTickets);
        setConflictTicketsModalOpen(true);
        return true;
      }
    }

    console.error("[ContactChat] Envio falhou", {
      status: err?.response?.status,
      data: err?.response?.data,
      message: err?.message,
    });
    return false;
  };
  if (!contact) {
    return null;
  }

  // Filtro do histórico aplicado ao MessagesList conforme o modo da empresa:
  // - ticket: "ver todas" (viewAll) ou um ticket específico
  // - whatsapp: uma conexão específica (ou padrão = conexões do usuário)
  const contactHistoryFilter = (() => {
    if (companyHistoryMode === "ticket") {
      if (historyTicketFilter === "all") {
        return canViewFullHistory ? { viewAll: true } : null;
      }
      return { ticketId: historyTicketFilter };
    }
    if (companyHistoryMode === "whatsapp" && historyWhatsappFilter !== "all") {
      return { whatsappId: historyWhatsappFilter };
    }
    return null;
  })();

  const historyTicketStatusLabel = (status) => {
    if (status === "open") return i18n.t("contactChat.history.status.open");
    if (status === "pending") return i18n.t("contactChat.history.status.pending");
    return i18n.t("contactChat.history.status.closed");
  };

  // Barra superior: botão de escolher a conexão + seletor de histórico do modo
  // da empresa (ticket/whatsapp). O botão fica aqui, ao lado do seletor, em vez
  // de no campo de mensagem, porque conexão e histórico são a mesma escolha.
  const renderHistoryFilterBar = () => {
    const connectionButton = historySelectorWhatsapps.length > 0 && (
      <Tooltip
        arrow
        placement="top"
        title={
          selectedConnection
            ? i18n.t("contactChat.connectionTooltip", { name: selectedConnection.name })
            : i18n.t("contactChat.chooseConnectionTooltip")
        }
      >
        <IconButton
          size="small"
          onClick={() => setConnDialogOpen(true)}
          style={{
            marginRight: 8,
            color: selectedWhatsappId ? "#25d366" : "#999",
          }}
        >
          <TouchAppIcon />
        </IconButton>
      </Tooltip>
    );

    let historySelector = null;

    if (companyHistoryMode === "ticket") {
      historySelector = (
        <FormControl variant="outlined" size="small" fullWidth>
          <Select
            value={historyTicketFilter}
            onChange={(e) => setHistoryTicketFilter(e.target.value)}
          >
            {canViewFullHistory ? (
              <MenuItem value="all">{i18n.t("contactChat.history.viewAll")}</MenuItem>
            ) : (
              historyTicketFilter === "all" && (
                <MenuItem value="all" disabled>{i18n.t("contactChat.history.selectTicket")}</MenuItem>
              )
            )}
            {contactTickets.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {i18n.t("contactChat.history.ticketOption", { id: t.id, status: historyTicketStatusLabel(t.status) }) +
                  `${t.whatsapp?.name ? ` • ${t.whatsapp.name}` : ""}` +
                  `${t.createdAt ? ` • ${new Date(t.createdAt).toLocaleDateString("pt-BR")}` : ""}`}
                {isInactiveTicket(t) && (
                  <InactiveConnectionBadge ticket={t} style={{ marginLeft: 6 }} />
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (companyHistoryMode === "whatsapp" && historySelectorWhatsapps.length > 0) {
      const selectorValue = historySelectorWhatsapps.some(
        (w) => Number(w.id) === Number(historyWhatsappFilter)
      )
        ? historyWhatsappFilter
        : "";
      historySelector = (
        <FormControl variant="outlined" size="small" fullWidth>
          <Select
            value={selectorValue}
            onChange={(e) => handleConnectionChange(e.target.value)}
          >
            {historySelectorWhatsapps.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                {`${w.name}${w.connectedNumber ? ` (${w.connectedNumber})` : ""}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (!connectionButton && !historySelector) return null;

    return (
      <Paper
        square
        style={{
          padding: "6px 12px",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          display: "flex",
          alignItems: "center",
        }}
      >
        {connectionButton}
        {historySelector ? (
          <div style={{ flex: 1, minWidth: 0 }}>{historySelector}</div>
        ) : (
          // Modo "por contato": sem seletor de histórico, mostra só qual conexão
          // será usada no envio
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="textSecondary" noWrap>
              {selectedConnection
                ? `${i18n.t("contactChat.connection", { name: selectedConnection.name })}${selectedConnection.connectedNumber ? ` (${selectedConnection.connectedNumber})` : ""}`
                : i18n.t("contactChat.autoConnection")}
            </Typography>
            {groupTicketElsewhere && (
              <Typography variant="caption" color="textSecondary" noWrap component="div">
                {i18n.t("contactChat.groupElsewhere", {
                  connection: groupTicketElsewhereConnName,
                  id: groupTicketElsewhere.id,
                  target: selectedConnection?.name || i18n.t("contactChat.thisConnection"),
                })}
              </Typography>
            )}
          </div>
        )}
      </Paper>
    );
  };

  const renderHeader = () => {
    if (!contact) {
      return null;
    }
    // PERF/UX: NÃO mostra skeleton ao trocar de contato. O `contact` recebido
    // por prop já tem name/number/avatar, então renderizamos o header de
    // imediato — sem máscara intermediária.

    // Mesma linha "NOMEFANTASIA - NOMECLI" do cabeçalho de chamado (TicketInfo).
    // A fonte é o contato carregado aqui, não o de prop: só o ShowContactService
    // traz contactClientes -> cliente (a listagem não). É também o objeto que o
    // socket company-${companyId}-contact atualiza, então a linha aparece assim
    // que um cliente é vinculado, sem reload.
    const clienteLabel = getClienteLabel(pseudoTicket?.contact || contact);

    return (
      <Card square className={classes.ticketHeader}>
        <IconButton
          className={classes.backButton}
          onClick={onClose}
        >
          <ArrowBackIcon />
        </IconButton>
        <CardHeader
          onClick={handleDrawerOpen}
          style={{ cursor: "pointer", flex: 1 }}
          titleTypographyProps={{ noWrap: true }}
          subheaderTypographyProps={{ noWrap: true }}
          avatar={<ContactAvatar contact={contact} />}
          title={
            existingTicket?.id
              ? `${contact?.name || ''} · #${existingTicket.id}`
              : (contact?.name || '')
          }
          subheader={
            <>
              {clienteLabel && (
                <Typography
                  variant="caption"
                  display="block"
                  noWrap
                  color="textSecondary"
                  title={clienteLabel}
                >
                  {clienteLabel}
                </Typography>
              )}
              {contact?.number || ''}
            </>
          }
        />
        <Tooltip title={i18n.t("messageSearchDrawer.title")}>
          <IconButton
            className={classes.headerIconButton}
            onClick={handleSearchDrawerOpen}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
        {/* Também sem chamado aberto: nesse caso o pseudoTicket vem com
            status "closed" e id "contact-<id>", e o TicketActionButtons se
            encarrega de deixar só o "..." com as ações de contato (vincular a
            cliente, agendar) — sem "Reabrir" nem nada que dependa de um
            chamado de verdade. */}
        {showCallWithoutTicket && (
          <Tooltip title={i18n.t("ticketActionButtons.tooltips.call")}>
            <IconButton
              className={classes.headerIconButton}
              onClick={handleCallWithoutTicket}
            >
              <CallIcon />
            </IconButton>
          </Tooltip>
        )}
        {/* Com chamado real, ligar assume como na tela do chamado (Aguardando
            abre e assume; aberto sem dono recebe o dono) e o chamado assumido
            aparece aqui mesmo. */}
        {pseudoTicket && (
          <TicketActionButtons
            ticket={pseudoTicket}
            assumeOnCall
            onTicketUpdated={showTicketInChat}
          />
        )}
      </Card>
    );
  };

  const renderMessagesList = () => {
    // UX: não gateia em `loading` — assim que existir um pseudoTicket
    // (do contato anterior ou recém-carregado) renderizamos. Evita o
    // flash de máscara ao alternar entre contatos.
    if (!pseudoTicket) {
      return null;
    }

    // "Abrir novo chamado" escolhido: o aviso sai e o input volta, criando um
    // chamado do atendente no primeiro envio (forceNewTicket).
    const blockedByOtherUser = isOtherUserTicket() && !forceNewTicketMode;
    const blockedByAgent = isAgentControlledTicket();
    // Grupo tem um chamado só por conversa: ali só existe "assumir".
    const newTicketButton = !pseudoTicket.isGroup && (
      <Button
        variant="outlined"
        className={classes.newTicketButton}
        onClick={() => setForceNewTicketMode(true)}
        startIcon={<AddIcon />}
      >
        {i18n.t("contactChat.otherUsers.createNew")}
      </Button>
    );

    // Se há tickets de outros usuários, exibir aviso com botões
    if (blockedByOtherUser) {
      // Se unificação está DESABILITADA e há múltiplos tickets, exibir um botão para cada
      if (!unifyTickets && otherUserTickets.length > 0) {
        return (
          <>
            <MessagesList
              key={`contact-${contact.id}`}
              ticket={pseudoTicket}
              ticketId={existingTicket?.id || pseudoTicket.id}
              isGroup={pseudoTicket.isGroup}
              contactId={contact.id}
              jumpToMessageId={activeJumpMessageId}
              jumpToMessageTs={activeJumpMessageTs}
              messageHistoryModeOverride="contact"
              contactHistoryFilter={contactHistoryFilter}
              viewerWhatsappId={selectedWhatsappId || existingTicket?.whatsappId || null}
            />
            <Box className={classes.otherUserWarning}>
              <Typography className={classes.warningText} style={{ marginBottom: 16 }}>
                <PersonIcon fontSize="small" />
                {i18n.t("contactChat.otherUsers.intro", { count: otherUserTickets.length })}
              </Typography>
              {otherUserTickets.map((ticket, index) => {
                const whatsappName = ticket.whatsapp?.name || "WhatsApp";
                const queueName = ticket.queue?.name || i18n.t("contactChat.otherUsers.noQueue");
                const userName = ticket.user?.name || i18n.t("contactChat.otherUsers.attendant");
                
                return (
                  <Box key={ticket.id} style={{ marginBottom: 12, width: '100%' }}>
                    <Typography variant="body2" style={{ marginBottom: 8, color: isDark ? theme.palette.text.secondary : "#666" }}>
                      <strong>Ticket #{ticket.id}</strong> - {whatsappName} • {queueName} • {userName}
                    </Typography>
                    <Button
                      variant="contained"
                      className={classes.assumeButton}
                      onClick={() => handleAssumeTicket(ticket)}
                      startIcon={<PersonIcon />}
                      fullWidth
                    >
                      {i18n.t("contactChat.otherUsers.assumeThis")}
                    </Button>
                  </Box>
                );
              })}
              {newTicketButton}
            </Box>
          </>
        );
      }
      
      // Comportamento padrão: um único botão
      const otherUserName = existingTicket?.user?.name || i18n.t("contactChat.otherUsers.anotherAttendant");
      return (
        <>
          <MessagesList
            key={`contact-${contact.id}`}
            ticket={pseudoTicket}
            ticketId={existingTicket?.id || pseudoTicket.id}
            isGroup={pseudoTicket.isGroup}
            contactId={contact.id}
            jumpToMessageId={activeJumpMessageId}
            jumpToMessageTs={activeJumpMessageTs}
            messageHistoryModeOverride="contact"
            contactHistoryFilter={contactHistoryFilter}
            viewerWhatsappId={selectedWhatsappId || existingTicket?.whatsappId || null}
          />
          <Box className={classes.otherUserWarning}>
            <Typography className={classes.warningText}>
              <PersonIcon fontSize="small" />
              {i18n.t("contactChat.otherUsers.belongsTo", { name: otherUserName })}
            </Typography>
            <Button
              variant="contained"
              className={classes.assumeButton}
              onClick={() => handleAssumeTicket(existingTicket)}
              startIcon={<PersonIcon />}
            >
              {i18n.t("contactChat.otherUsers.assume")}
            </Button>
            {newTicketButton}
          </Box>
        </>
      );
    }

    if (blockedByAgent) {
      const resolvedAgentName = agentName || existingTicket?.prompt?.name || i18n.t("contactChat.agent.fallbackName", { id: existingTicket?.promptId });
      return (
        <>
          <MessagesList
            key={`contact-${contact.id}`}
            ticket={pseudoTicket}
            ticketId={existingTicket?.id || pseudoTicket.id}
            isGroup={pseudoTicket.isGroup}
            contactId={contact.id}
            jumpToMessageId={activeJumpMessageId}
            jumpToMessageTs={activeJumpMessageTs}
            messageHistoryModeOverride="contact"
            contactHistoryFilter={contactHistoryFilter}
            viewerWhatsappId={selectedWhatsappId || existingTicket?.whatsappId || null}
          />
          <Box className={classes.otherUserWarning}>
            <Typography
              className={classes.warningText}
              style={aiTypingAgent ? { fontStyle: "italic", color: isDark ? teal[300] : "#075e54" } : undefined}
            >
              {aiTypingAgent ? <RobotIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
              {aiTypingAgent
                ? i18n.t("contactChat.agent.typing", { name: resolvedAgentName })
                : i18n.t("contactChat.agent.handling", { name: resolvedAgentName })}
            </Typography>
            <Button
              variant="contained"
              className={classes.assumeButton}
              onClick={() => handleAssumeTicket(existingTicket)}
              startIcon={<PersonIcon />}
            >
              {i18n.t("contactChat.otherUsers.assume")}
            </Button>
          </Box>
        </>
      );
    }

    const messageInputWhatsapp = selectedConnection || existingTicket?.whatsapp || null;

    return (
      <>
        <MessagesList
          key={`contact-${contact.id}`}
          ticket={pseudoTicket}
          ticketId={existingTicket?.id || pseudoTicket.id}
          isGroup={pseudoTicket.isGroup}
          contactId={contact.id}
          jumpToMessageId={activeJumpMessageId}
          jumpToMessageTs={activeJumpMessageTs}
          messageHistoryModeOverride="contact"
          contactHistoryFilter={contactHistoryFilter}
          viewerWhatsappId={selectedWhatsappId || existingTicket?.whatsappId || null}
        />
        <MessageInput
          // "Abrir novo chamado": sem o id do chamado do outro atendente, o
          // envio vai pela rota por contato (que cria o chamado novo).
          ticketId={forceNewTicketMode ? `contact-${contact.id}` : (existingTicket?.id || pseudoTicket.id)}
          ticketStatus="open"
          isGroup={pseudoTicket.isGroup}
          contactId={contact.id}
          forceNewTicket={forceNewTicketMode}
          onMessageSent={handleMessageSent}
          onSendError={handleSendError}
          onTicketCreated={onTicketCreated}
          whatsapps={visibleWhatsapps}
          selectedWhatsappId={selectedWhatsappId}
          whatsapp={messageInputWhatsapp}
          cloudApiWindowOpen={selectedCloudApiWindowOpen}
        />
      </>
    );
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen || searchDrawerOpen,
        })}
      >
        {renderHeader()}
        {renderHistoryFilterBar()}
        {existingTicket?.id && pseudoTicket && (
          <Paper square>
            <TagsContainer ticket={pseudoTicket} />
          </Paper>
        )}
        {existingTicket?.id && (
          <ConnectionDisconnectedBanner
            whatsappId={existingTicket.whatsappId}
            whatsapp={
              whatsapps.find((w) => Number(w.id) === Number(existingTicket.whatsappId)) ||
              existingTicket.whatsapp
            }
            ticketStatus={existingTicket.status}
          />
        )}
        <ReplyMessageProvider>{renderMessagesList()}</ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={pseudoTicket?.contact || contact}
        loading={loading}
        ticket={pseudoTicket}
        // Modo "Por ticket": a galeria segue o ticket escolhido no seletor do
        // histórico (o backend só aplica o filtro a quem não tem histórico completo)
        galleryTicketId={
          companyHistoryMode === "ticket" && historyTicketFilter !== "all"
            ? historyTicketFilter
            : null
        }
        // Aba Mídia e visualizador: mesmo recorte do histórico exibido ao lado.
        contactHistoryFilter={contactHistoryFilter}
        onJumpToMessage={handleJumpToMessageFromDrawer}
      />
      <MessageSearchDrawer
        open={searchDrawerOpen}
        handleDrawerClose={handleSearchDrawerClose}
        contactId={contact?.id}
        onJumpToMessage={handleJumpToMessageFromDrawer}
      />

      <ConnectionSelectDialog
        open={connDialogOpen}
        onClose={() => setConnDialogOpen(false)}
        whatsapps={historySelectorWhatsapps}
        selectedWhatsappId={selectedWhatsappId}
        // No modo por conexão o histórico precisa de uma conexão concreta,
        // então "Automático" só faz sentido nos demais modos
        allowAutomatic={companyHistoryMode !== "whatsapp"}
        onSelect={(w) => handleConnectionChange(w ? w.id : "")}
        title={i18n.t("contactChat.selectConnection")}
      />
      <ConnectionSelectDialog
        open={callConnDialogOpen}
        onClose={() => setCallConnDialogOpen(false)}
        whatsapps={callableWhatsapps}
        selectedWhatsappId={selectedWhatsappId}
        onSelect={(w) => w && chooseCallQueue(w.id)}
        title={i18n.t("contactChat.callConnectionTitle")}
      />
      <QueueSelectDialog
        open={callQueueWhatsappId !== null}
        queues={user?.queues || []}
        onSelect={(queueId) => {
          const whatsappId = callQueueWhatsappId;
          setCallQueueWhatsappId(null);
          startCallWithoutTicket(whatsappId, queueId);
        }}
        onClose={() => setCallQueueWhatsappId(null)}
      />

      {/* Cloud API - Template picker dialog */}
      <Dialog
        open={conflictTicketsModalOpen}
        onClose={handleCloseConflictTicketsModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{i18n.t("contactChat.otherUsers.dialogTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 16 }}>
            {conflictCanCreateNew
              ? i18n.t("contactChat.otherUsers.dialogIntroWithNew")
              : i18n.t("contactChat.otherUsers.dialogIntro")}
          </DialogContentText>

          {conflictTickets.map((ticket) => (
            <Box
              key={`conflict-ticket-${ticket.id}`}
              style={{
                border: `1px solid ${isDark ? theme.palette.divider : "#e0e0e0"}`,
                borderRadius: 8,
                padding: 12,
                marginBottom: 10,
              }}
            >
              <Typography variant="body2" style={{ marginBottom: 8, color: isDark ? theme.palette.text.secondary : "#666" }}>
                <strong>Ticket #{ticket.id}</strong> - {ticket.whatsappName || "WhatsApp"} • {ticket.queueName || i18n.t("contactChat.otherUsers.noQueue")} • {ticket.userName || i18n.t("contactChat.otherUsers.attendant")}
              </Typography>
              <Button
                variant="contained"
                className={classes.assumeButton}
                onClick={() => handleAssumeTicket(ticket)}
                startIcon={<PersonIcon />}
                fullWidth
              >
                {i18n.t("contactChat.otherUsers.assume")}
              </Button>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConflictTicketsModal}>{i18n.t("common.cancel")}</Button>
          {conflictCanCreateNew && (
            <Button color="primary" variant="outlined" onClick={handleCreateNewFromConflict} startIcon={<AddIcon />}>
              {i18n.t("contactChat.otherUsers.createNew")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog
        open={templatePickerOpen}
        onClose={handleCloseTemplatePicker}
        maxWidth="sm"
        fullWidth
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
      >
        <DialogTitle>{i18n.t("contactChat.templatePicker.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 16 }}>
            {i18n.t("contactChat.templatePicker.description")}
          </DialogContentText>
          {loadingTemplates ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : cloudApiTemplates.length === 0 ? (
            <DialogContentText>
              {i18n.t("contactChat.templatePicker.noTemplates")}
            </DialogContentText>
          ) : (
            <>
              <FormControl fullWidth variant="outlined" margin="dense">
                <InputLabel>{i18n.t("contactChat.templatePicker.selectTemplate")}</InputLabel>
                <Select
                  value={selectedCloudApiTemplate}
                  onChange={(e) => handleSelectCloudApiTemplate(e.target.value)}
                  label={i18n.t("contactChat.templatePicker.selectTemplate")}
                >
                  {cloudApiTemplates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.displayName || t.name} ({t.language}) - {t.category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(() => {
                const selObj = cloudApiTemplates.find((t) => t.id === parseInt(selectedCloudApiTemplate));
                if (!selObj) return null;
                return (
                  <Box mt={2} p={2} bgcolor="background.default" border={1} borderColor="divider" borderRadius={4}>
                    <Typography variant="caption" color="textSecondary">
                      {i18n.t("contactChat.templatePicker.preview")}
                    </Typography>
                    <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                      {selObj.bodyText || i18n.t("contactChat.templatePicker.emptyBody")}
                    </Typography>
                  </Box>
                );
              })()}

              {cloudApiTemplateVars.length > 0 && (
                <Box mt={2} key="template-vars">
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("contactChat.templatePicker.variables")}
                  </Typography>
                  {cloudApiTemplateVars.map((varItem, index) => (
                    <TextField
                      key={`var-${varItem.name}-${index}`}
                      label={`${i18n.t("contactChat.templatePicker.variable")} {{${varItem.name}}}`}
                      value={varItem.value}
                      onChange={(e) => {
                        const newVars = [...cloudApiTemplateVars];
                        newVars[index].value = e.target.value;
                        setCloudApiTemplateVars(newVars);
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      style={{ marginTop: 8 }}
                    />
                  ))}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTemplatePicker}>{i18n.t("common.cancel")}</Button>
          <Button
            onClick={handleSendCloudApiTemplate}
            color="primary"
            variant="contained"
            disabled={!selectedCloudApiTemplate || sendingTemplate || loadingTemplates}
          >
            {sendingTemplate ? i18n.t("contactChat.templatePicker.sending") : i18n.t("contactChat.templatePicker.send")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ContactChat;
